import { useEffect, useMemo, useRef } from 'react';
import type React from 'react';
import {
  Alert,
  AlertDescription,
  Button,
  Skeleton,
} from '@roger/ui-components';
import {
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { FeatureFlag } from '../../constants/featureFlags';
import type { ResultRow } from '../../reporting/arrowToRows';
import {
  axisIdForKind,
  MAX_DATE_CATEGORIES,
} from '../../reporting/chartFormat';
import {
  relatedDatasetsFor,
  type RelatedManifests,
} from '../../reporting/columnRefs';
import {
  columnLabelRefs,
  columnMetaByRef,
  defaultColumnLabels,
  mergeColumnLabels,
} from '../../reporting/columnNames';
import { dimAlias, validateDefinition } from '../../reporting/compileReport';
import {
  backfillJoinedDatasets,
  resultColumnKeys,
} from '../../reporting/definitionUpdates';
import {
  dimensionFormats,
  formatValue,
  humanizeHeader,
  resolveFormatKind,
} from '../../reporting/formatValue';
import { useReportRunner } from '../../reporting/useReportRunner';
import { useStore } from '../../store';
import {
  TABLE_ROW_LIMIT,
  additiveMeasureAliases,
  measureTotal,
  oldestFirst,
  widgetFitsReport,
  widgetPeriod,
} from '../../reporting/widgetForms';
import type {
  DashboardTabWidgetItem,
  DatasetManifestEntry,
  ReportDefinition,
  ReportingManifest,
  ReportWidget,
} from '../../types/reporting';
import ReportScopeNotice from '../reporting/ReportScopeNotice';
import ResultsChart from '../reporting/ResultsChart';
import ResultsTable from '../reporting/ResultsTable';
import { PieListWidget } from './board/BoardWidgets';
import { chartColor } from './board/chartColors';
import { parseISOToLocalDate } from '../../utils/dateUtils';

// A card shows this many bars; the report has the rest.
export const BAR_WIDGET_LIMIT = 8;

const byMeasureDesc = (measure: string) => (a: ResultRow, b: ResultRow) =>
  Number(b[measure] ?? 0) - Number(a[measure] ?? 0);

const EmptyReport = () => (
  <p className='text-sm text-muted-foreground'>No data for this report.</p>
);

// The bars a card shows: the newest on a date axis, oldest first; the
// report's order when it sets a sort; else the largest first.
function widgetBars(
  rows: ResultRow[],
  measure: string,
  dateDimension: string | null,
  keepOrder: boolean
): ResultRow[] {
  if (dateDimension) {
    return oldestFirst(rows, dateDimension).slice(-BAR_WIDGET_LIMIT);
  }
  const ordered = keepOrder ? rows : [...rows].sort(byMeasureDesc(measure));
  return ordered.slice(0, BAR_WIDGET_LIMIT);
}

interface BodyProps {
  widget: ReportWidget;
  definition: ReportDefinition;
  dataset: DatasetManifestEntry;
  related: RelatedManifests;
  columns: string[];
  rows: ResultRow[];
}

// Only totals: an "Average of" label says what the number means.
const TOTAL_PREFIX = /^(sum|total|count)( of)?\s+/;

// Whether a measure label and a title name the same total.
function sameMeasure(label: string, title: string): boolean {
  const plain = (text: string) =>
    text.trim().toLowerCase().replace(TOTAL_PREFIX, '');
  const a = plain(label);
  return !!a && a === plain(title);
}

export function ReportWidgetBody({
  widget,
  definition,
  dataset,
  related,
  columns,
  rows,
}: BodyProps) {
  const columnLabels = useMemo(() => {
    const defaults = defaultColumnLabels(
      dataset,
      related,
      columnLabelRefs(definition, dataset)
    );
    return mergeColumnLabels(
      defaults,
      definition.column_labels,
      resultColumnKeys(definition, dataset)
    );
  }, [dataset, related, definition]);
  const columnMeta = useMemo(
    () => columnMetaByRef(dataset, related),
    [dataset, related]
  );
  const dimFormats = useMemo(
    () => dimensionFormats(definition.group_bys),
    [definition]
  );
  const dimensionKeys = definition.group_bys.map(dimAlias);
  const measure = widget.display.measure ?? '';
  const pivotColumn = definition.pivot_column?.column;
  // The card title names the widget, so a bare "Count" adds nothing.
  const unitLabel = (alias: string) => {
    const label = humanizeHeader(alias, columnLabels, columnMeta);
    return label === 'Count' ? '' : label;
  };
  const dimensionLabel = (row: ResultRow) =>
    dimensionKeys
      .map(key =>
        formatValue(
          row[key],
          resolveFormatKind(key, columnMeta, dimFormats.get(key)),
          {
            dateGranularity: dimFormats.get(key)?.dateGranularity,
            emptyText: 'None',
          }
        )
      )
      .join(' · ');

  if (widget.form === 'number') {
    const kindOf = (alias: string) => resolveFormatKind(alias, columnMeta);
    const show = (alias: string) =>
      formatValue(
        measureTotal(columns, rows, dimensionKeys, alias),
        kindOf(alias)
      );
    const subtext = widget.display.subtext_measure;
    // A bare "Count" counts the dataset's rows, so it names them ("Tasks").
    const subtextLabel = (subtext && unitLabel(subtext)) || dataset.label;
    const unit = unitLabel(measure);
    // No label when it only repeats the title ("Sum of Delivered points").
    const label = unit && !sameMeasure(unit, widget.name) ? unit : '';
    // The board's Stat look: a big number over a one-line label, set above
    // centered in the card, but never more than 3rem below the header.
    return (
      <div className='flex flex-1 flex-col text-center'>
        <div className='max-h-12 flex-[1_1_0]' />
        <p
          data-testid='widget-number'
          className='dash-big-number text-4xl font-semibold leading-none tabular-nums'
        >
          {show(measure)}
        </p>
        {label ? (
          <p className='mt-1.5 truncate text-sm text-muted-foreground'>
            {label}
          </p>
        ) : null}
        {subtext ? (
          <p className='mt-0.5 truncate text-xs text-muted-foreground'>
            {subtextLabel}: {show(subtext)}
          </p>
        ) : null}
        <div className='flex-[1_1_0]' />
      </div>
    );
  }

  if (widget.form === 'bar') {
    const [first] = dimensionKeys;
    const dateDimension =
      dimensionKeys.length === 1 &&
      resolveFormatKind(first, columnMeta, dimFormats.get(first)) === 'date'
        ? first
        : null;
    // A bar widget stores no measure. The board's bars show one value of zero
    // or more, so several values or a negative one keep the grouped chart.
    const values = columns.filter(c => !dimensionKeys.includes(c));
    const value = measure || values[0] || '';
    const bars = widgetBars(
      rows,
      value,
      dateDimension,
      definition.sort.length > 0
    );
    const more = rows.length - bars.length;
    const grouped =
      values.length > 1 || rows.some(row => Number(row[value] ?? 0) < 0);
    let chart: React.ReactNode;
    if (grouped) {
      chart = (
        <ResultsChart
          columns={columns}
          rows={bars}
          dimensionKeys={dimensionKeys}
          columnLabels={columnLabels}
          columnMeta={columnMeta}
          dimFormats={dimFormats}
          pivotColumn={pivotColumn}
          narrow
        />
      );
    } else {
      if (bars.length === 0) return <EmptyReport />;
      const valueKind = resolveFormatKind(value, columnMeta);
      chart = (
        <PieListWidget
          denominator={rows.reduce(
            (sum, row) => sum + Number(row[value] ?? 0),
            0
          )}
          unit={unitLabel(value)}
          formatCount={count => formatValue(count, valueKind)}
          showPercent={additiveMeasureAliases(definition).includes(value)}
          items={bars.map((row, index) => ({
            id: `bar-${index}`,
            label: dimensionLabel(row),
            count: Number(row[value] ?? 0),
          }))}
          view='list'
          chartLabel={widget.name}
          itemLimit={BAR_WIDGET_LIMIT}
        />
      );
    }
    return (
      <div className={`flex flex-col gap-2${grouped ? ' min-h-0' : ''}`}>
        {chart}
        {more > 0 ? (
          <p className='text-xs text-muted-foreground'>
            + {more.toLocaleString()} more in the report
          </p>
        ) : null}
      </div>
    );
  }

  if (widget.form === 'line') {
    if (rows.length > MAX_DATE_CATEGORIES) {
      return (
        <p role='status' className='text-sm text-muted-foreground'>
          Too many days to chart — group by week or month, or shorten the date
          window.
        </p>
      );
    }
    const dimension = dimensionKeys[0];
    const seriesKeys = columns.filter(c => !dimensionKeys.includes(c));
    const dimKind = resolveFormatKind(
      dimension,
      columnMeta,
      dimFormats.get(dimension)
    );
    const tick = (value: unknown) =>
      formatValue(value, dimKind, {
        dateGranularity: dimFormats.get(dimension)?.dateGranularity,
      });
    // Month ticks read "Jul", with the year on the first tick and where it
    // changes ("Jan 2027"). Recharts formats the shown ticks in order, from 0.
    let lastYear: number | null = null;
    const axisTick = (value: unknown, index: number) => {
      const date =
        dimFormats.get(dimension)?.dateGranularity === 'month'
          ? parseISOToLocalDate(String(value))
          : null;
      if (!date) return tick(value);
      const year = date.getFullYear();
      const month = date.toLocaleString('en-US', { month: 'short' });
      const showYear = index === 0 || year !== lastYear;
      lastYear = year;
      return showYear ? `${month} ${year}` : month;
    };
    // Money and counts get their own Y axes, like the builder's charts.
    const kinds = seriesKeys.map(key => resolveFormatKind(key, columnMeta));
    const units = {
      hasMoney: kinds.some(kind => axisIdForKind(kind) === 'money'),
      hasCount: kinds.some(kind => axisIdForKind(kind) === 'count'),
    };
    const mixed = units.hasMoney && units.hasCount;
    const countAxisId = 'count';
    const moneyAxisId = 'money';
    const axis = {
      fontSize: 11,
      stroke: 'hsl(var(--muted-foreground))',
      tickLine: false,
      axisLine: { stroke: 'hsl(var(--border))' },
    };
    // Only the two axis lines, as an L; the axes size to their ticks.
    const yAxis = { ...axis, width: 'auto' as const };
    // Starts at 14rem and fills or shrinks with a fixed-height card; a stacked
    // card or a preview has no height to fill, so it stays 14rem.
    return (
      <div className='h-56 min-h-20 w-full flex-[1_1_14rem]'>
        <ResponsiveContainer width='100%' height='100%'>
          <LineChart
            data={oldestFirst(rows, dimension)}
            margin={{ top: 4, right: 8, bottom: 0, left: 0 }}
          >
            <XAxis
              dataKey={dimension}
              tickFormatter={axisTick}
              height={20}
              {...axis}
            />
            {units.hasCount ? <YAxis yAxisId={countAxisId} {...yAxis} /> : null}
            {units.hasMoney ? (
              <YAxis
                yAxisId={moneyAxisId}
                orientation={mixed ? 'right' : 'left'}
                tickFormatter={(value: unknown) => formatValue(value, 'money')}
                {...yAxis}
              />
            ) : null}
            <Tooltip
              cursor={{ stroke: 'hsl(var(--border))' }}
              content={({ active, payload, label }) =>
                active && payload?.length ? (
                  <div className='rounded-lg border bg-popover px-3 py-2 text-sm text-popover-foreground shadow-md'>
                    <div className='font-semibold'>{tick(label)}</div>
                    {payload.map(entry => {
                      const index = seriesKeys.indexOf(String(entry.dataKey));
                      const value = formatValue(entry.value, kinds[index]);
                      const name = unitLabel(String(entry.dataKey));
                      return (
                        <div
                          key={String(entry.dataKey)}
                          className='mt-1 text-muted-foreground'
                        >
                          {name ? `${name}: ${value}` : value}
                        </div>
                      );
                    })}
                  </div>
                ) : null
              }
            />
            {seriesKeys.length > 1 ? (
              <Legend wrapperStyle={{ fontSize: 12 }} />
            ) : null}
            {seriesKeys.map((key, index) => (
              <Line
                key={key}
                yAxisId={axisIdForKind(kinds[index])}
                dataKey={key}
                name={humanizeHeader(key, columnLabels, columnMeta)}
                stroke={chartColor(index)}
                dot={false}
                type='monotone'
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (widget.form === 'pie') {
    const positive = rows.filter(row => Number(row[measure] ?? 0) >= 0);
    const hasNegative = positive.length < rows.length;
    const items = positive.sort(byMeasureDesc(measure)).map((row, index) => ({
      id: `slice-${index}`,
      label: dimensionLabel(row),
      count: Number(row[measure] ?? 0),
    }));
    const unit = humanizeHeader(measure, columnLabels, columnMeta);
    const measureKind = resolveFormatKind(measure, columnMeta);
    const nonZero = items.filter(item => item.count > 0);
    // One slice is the whole pie, so say it in a line.
    if (nonZero.length === 1 && !hasNegative) {
      return (
        <div className='space-y-1 py-2'>
          <p className='text-sm font-medium'>{nonZero[0].label}</p>
          <p className='text-2xl font-semibold'>
            {formatValue(nonZero[0].count, measureKind)}
          </p>
          <p className='text-xs text-muted-foreground'>100% of {unit}</p>
        </div>
      );
    }
    return (
      <div className='flex flex-1 flex-col gap-2'>
        {nonZero.length === 0 ? (
          <EmptyReport />
        ) : (
          <PieListWidget
            denominator={items.reduce((sum, item) => sum + item.count, 0)}
            unit={unitLabel(measure)}
            formatCount={count => formatValue(count, measureKind)}
            items={items}
            view='chart'
            chartLabel={widget.name}
            itemLimit={6}
          />
        )}
        {hasNegative ? (
          <p role='status' className='text-xs text-muted-foreground'>
            Negative values are not shown in this chart.
          </p>
        ) : null}
      </div>
    );
  }

  const rowLimit = widget.display.row_limit ?? TABLE_ROW_LIMIT.default;
  const more = rows.length - rowLimit;
  return (
    <div className='flex flex-col gap-2'>
      <ResultsTable
        compact
        columns={columns}
        rows={rows.slice(0, rowLimit)}
        columnLabels={columnLabels}
        columnMeta={columnMeta}
        dimFormats={dimFormats}
        pivotColumn={pivotColumn}
        groupByColumns={dimensionKeys}
      />
      {more > 0 ? (
        <p className='text-xs text-muted-foreground'>
          + {more.toLocaleString()} more rows
        </p>
      ) : null}
    </div>
  );
}

interface ReportWidgetCardProps {
  item: DashboardTabWidgetItem;
  manifest: ReportingManifest;
  currentUserId: string | null;
  refreshNonce: number;
  onEdit: () => void;
  // The data's snapshot time, the report's period and a failed Refresh, for
  // the card's menu.
  onDetails?: (details: WidgetDetails) => void;
}

export type WidgetDetails = {
  builtAt: string | null;
  period: string | null;
  refreshFailed?: boolean;
};

// A widget's body on a dashboard card. The card around it has the title and
// the menu, which shows the details onDetails reports.
export default function ReportWidgetCard({
  item,
  manifest,
  currentUserId,
  refreshNonce,
  onEdit,
  onDetails,
}: ReportWidgetCardProps) {
  const widget = item.widget;
  const report = item.report;
  const dataset = useMemo(
    () =>
      report
        ? (manifest.datasets.find(d => d.key === report.dataset_key) ?? null)
        : null,
    [manifest, report]
  );
  const related = useMemo(
    () => relatedDatasetsFor(dataset, manifest),
    [dataset, manifest]
  );
  // The builder validates a report when it opens it. A card has no open step,
  // so it validates here and shows the dropped columns itself.
  const validated = useMemo(
    () =>
      report && dataset
        ? validateDefinition(report.definition, dataset, related)
        : null,
    [report, dataset, related]
  );
  // A legacy report lists no joins. Backfill them, as the builder does.
  const definition = useMemo(
    () =>
      validated ? backfillJoinedDatasets(validated.definition, related) : null,
    [validated, related]
  );
  const joined = useMemo(
    () =>
      (definition?.joined_datasets ?? []).flatMap(key => {
        const entry = related.get(key);
        return entry ? [entry] : [];
      }),
    [definition, related]
  );
  const fits =
    item.status === 'ok' &&
    !!widget &&
    !!definition &&
    !!dataset &&
    widgetFitsReport(widget, definition, dataset, related);

  const paymentOasisUpdatedBehavior = useStore(state =>
    state.hasFeatureFlag(FeatureFlag.PAYMENT_OASIS_UPDATED_BEHAVIOR)
  );
  const runner = useReportRunner(fits ? definition : null, dataset, related, {
    paymentOasisUpdatedBehavior,
  });

  // The runner forces a rebuild for its own scope. Forced rebuilds of the same
  // dataset and scope share one request, so cards on one dataset do not stack.
  const refreshData = useRef(runner.refreshData);
  refreshData.current = runner.refreshData;
  const seenNonce = useRef(refreshNonce);
  useEffect(() => {
    if (seenNonce.current === refreshNonce) return;
    seenNonce.current = refreshNonce;
    refreshData.current();
  }, [refreshNonce]);

  const noAccess = item.status === 'no_access' || !dataset;
  let message: string | null = null;
  if (item.status === 'report_not_shared') {
    message = 'This report is no longer shared.';
  } else if (noAccess) {
    message = "You do not have access to this report's data.";
  } else if (!fits) {
    message = 'This widget no longer matches its report.';
  }

  let content: React.ReactNode;
  if (message) {
    content = (
      <div className='space-y-2'>
        <p role='status' className='text-sm text-muted-foreground'>
          {message}
        </p>
        {!noAccess &&
        item.status === 'ok' &&
        currentUserId === item.created_by ? (
          <Button type='button' variant='outline' size='sm' onClick={onEdit}>
            Edit
          </Button>
        ) : null}
      </div>
    );
  } else if (runner.status === 'ready' && widget && definition && dataset) {
    const dropped = [
      ...new Set([
        ...(validated?.dropped ?? []),
        ...(runner.droppedColumns ?? []),
      ]),
    ];
    content = (
      <>
        <ReportScopeNotice dataset={dataset} joined={joined} />
        {dropped.length > 0 ? (
          <p role='status' className='text-xs text-muted-foreground'>
            This report used columns that are no longer available, so the data
            may be broader than intended: {dropped.join(', ')}.
          </p>
        ) : null}
        <ReportWidgetBody
          widget={widget}
          definition={definition}
          dataset={dataset}
          related={related}
          columns={runner.columns}
          rows={runner.rows}
        />
      </>
    );
  } else if (runner.status === 'loading' && runner.buildingProgress) {
    content = (
      <p role='status' className='text-sm text-muted-foreground'>
        Preparing data…
      </p>
    );
  } else if (runner.status === 'idle' || runner.status === 'loading') {
    content = <Skeleton className='h-24 w-full' />;
  } else {
    content = (
      <Alert variant='destructive'>
        <AlertDescription className='flex items-center justify-between gap-2'>
          Could not load this widget
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={runner.reload}
          >
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  const builtAt =
    !message && runner.status === 'ready' && widget ? runner.builtAt : null;
  const period =
    !message && definition && dataset
      ? widgetPeriod(definition, dataset, related)
      : null;
  const refreshFailed = !!builtAt && !!runner.refreshFailed;
  useEffect(() => {
    onDetails?.({ builtAt, period, refreshFailed });
  }, [onDetails, builtAt, period, refreshFailed]);

  return (
    <section
      className='flex h-full min-h-0 min-w-0 flex-col gap-2'
      data-testid={`report-widget-${item.widget_id}`}
      aria-label={item.name}
    >
      {/* Fills the card, so a chart can scroll its bars under a fixed scale. */}
      <div className='flex min-h-0 min-w-0 flex-1 flex-col gap-2 overflow-auto'>
        {content}
      </div>
    </section>
  );
}
